import React, { useState } from 'react';
import { differenceInDays, differenceInWeeks, differenceInMonths, differenceInYears, differenceInHours, differenceInSeconds, getWeek, getDayOfYear, isLeapYear, startOfWeek, endOfWeek, addWeeks } from 'date-fns';
import { Calculator, CalendarCheck } from 'lucide-react';

const DateTools: React.FC = () => {
  const [start, setStart] = useState<string>(new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState<string>(new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().slice(0, 10));
  
  const [yearCheck, setYearCheck] = useState<number>(new Date().getFullYear());

  const startDate = new Date(start);
  const endDate = new Date(end);
  
  const diffYears = differenceInYears(endDate, startDate);
  const diffMonths = differenceInMonths(endDate, startDate);
  const diffWeeks = differenceInWeeks(endDate, startDate);
  const diffDays = differenceInDays(endDate, startDate);
  const diffHours = differenceInHours(endDate, startDate);
  const diffSeconds = differenceInSeconds(endDate, startDate);

  const currentYear = new Date().getFullYear();
  const currentWeek = getWeek(new Date());
  const currentDayOfYear = getDayOfYear(new Date());

  const weeksInYear = [];
  const startOfSelectedYear = new Date(yearCheck, 0, 1);
  for (let i = 0; i < 53; i++) {
    const d = addWeeks(startOfSelectedYear, i);
    if (d.getFullYear() !== yearCheck && i > 50) break; // Simple check to not overflow too much
    const w = getWeek(d);
    // Standard ISO week logic can be complex, using simple week generation
    const s = startOfWeek(d, { weekStartsOn: 1 });
    const e = endOfWeek(d, { weekStartsOn: 1 });
    weeksInYear.push({ num: w, start: s, end: e });
  }

  return (
    <div className="space-y-8">
      
      {/* Diff Calc */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
          <Calculator size={24} className="text-sky-500" /> Date Difference Calculator
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-6 mb-8">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-600 mb-1">Start Date</label>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-600 mb-1">End Date</label>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatBox label="Years" value={diffYears} />
          <StatBox label="Months" value={diffMonths} />
          <StatBox label="Weeks" value={diffWeeks} />
          <StatBox label="Days" value={diffDays} />
          <StatBox label="Hours" value={diffHours} />
          <StatBox label="Seconds" value={diffSeconds} />
        </div>
      </div>

      {/* Current Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-slate-900 text-white p-6 rounded-xl shadow-lg lg:col-span-1">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><CalendarCheck size={18}/> Today's Status</h3>
          <div className="space-y-4">
             <div className="flex justify-between border-b border-slate-700 pb-2">
               <span className="text-slate-400">Week Number</span>
               <span className="font-mono font-bold text-sky-400">{currentWeek}</span>
             </div>
             <div className="flex justify-between border-b border-slate-700 pb-2">
               <span className="text-slate-400">Day of Year</span>
               <span className="font-mono font-bold text-sky-400">{currentDayOfYear} / {isLeapYear(new Date()) ? 366 : 365}</span>
             </div>
             <div className="flex justify-between border-b border-slate-700 pb-2">
               <span className="text-slate-400">Leap Year</span>
               <span className="font-mono font-bold text-sky-400">{isLeapYear(new Date()) ? 'Yes' : 'No'}</span>
             </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 lg:col-span-2">
          <h3 className="text-lg font-bold text-slate-800 mb-4">Week Numbers for Year</h3>
          <div className="flex items-center gap-4 mb-4">
            <input 
              type="number" 
              value={yearCheck} 
              onChange={e => setYearCheck(parseInt(e.target.value))} 
              className="w-32 px-4 py-2 bg-white border border-slate-300 rounded-lg"
            />
            <span className="text-sm text-slate-500">{isLeapYear(new Date(yearCheck, 0, 1)) ? 'Is a Leap Year (366 days)' : 'Common Year (365 days)'}</span>
          </div>
          
          <div className="h-64 overflow-y-auto pr-2">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-slate-500">Week</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-500">Start (Mon)</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-500">End (Sun)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {weeksInYear.map((w, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-bold text-slate-700">{w.num}</td>
                    <td className="px-4 py-2 text-slate-600">{w.start.toLocaleDateString()}</td>
                    <td className="px-4 py-2 text-slate-600">{w.end.toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value }: { label: string, value: number }) => (
  <div className="bg-slate-50 p-4 rounded-lg text-center border border-slate-100">
    <div className="text-xs text-slate-400 uppercase font-bold mb-1">{label}</div>
    <div className="text-xl font-mono font-bold text-slate-800 truncate" title={value.toString()}>
      {value.toLocaleString()}
    </div>
  </div>
);

export default DateTools;